/**
 * Turning a name the model proposed into a real `parent_organizations`
 * document, or into an alias on one that already exists.
 *
 * The decision part is here, away from Firestore and React, because it is the
 * part that can be wrong in a way nobody notices: which id a name takes, and
 * what happens when that id is already somebody else's.
 */
import type { ParentOrganization } from '../../types';
import { parentOrgMatchKey, slugifyParentOrgValue } from './parentOrgSeed';

/** What a quick-create would do, decided before anything is written. */
export type QuickCreatePlan =
  | {
      kind: 'create';
      /** The doc id to be created — shown to the curator before the write. */
      id: string;
      name: string;
      /** The raw proposed name, preserved verbatim as the first alias. */
      alternativeNames: string[];
    }
  | {
      kind: 'collision';
      /** The id the name wants, already taken by `existing`. */
      id: string;
      name: string;
      existing: { id: string; label: string };
      /** True when `existing` already answers to this name — nothing to do. */
      alreadyKnown: boolean;
    }
  | { kind: 'unusable'; name: string };

function labelOf(org: Pick<ParentOrganization, 'id' | 'name' | 'displayName'>): string {
  return org.displayName || org.name || org.id;
}

/**
 * Decide what creating `name` as an organization would mean.
 *
 * The id comes from `slugifyParentOrgValue` — the same slug the seed mints, so a
 * name quick-created here and the same name seeded from the lookup land on ONE
 * document rather than two spellings of one company. There is no separate `slug`
 * field to write: the document id IS the slug, per the type.
 *
 * A taken id is reported as a collision rather than an error. Two things reach
 * this path — the org genuinely exists under another spelling ("Rolex S.A." when
 * `rolex-sa` is there), or a different company slugs the same way — and both are
 * better answered by showing the curator the existing organization than by a
 * failed toast. `alreadyKnown` separates the case where the alias is already
 * recorded, which needs no write at all.
 */
export function planQuickCreate(
  name: string,
  orgs: ReadonlyArray<ParentOrganization>,
): QuickCreatePlan {
  const trimmed = (name ?? '').trim();
  const id = slugifyParentOrgValue(trimmed);
  if (!trimmed || !id) return { kind: 'unusable', name: trimmed };

  const existing = orgs.find((o) => o.id === id);
  if (existing) {
    const known = new Set(
      [existing.name, existing.displayName, ...(existing.alternativeNames ?? [])]
        .map((v) => parentOrgMatchKey(v ?? ''))
        .filter(Boolean),
    );
    return {
      kind: 'collision',
      id,
      name: trimmed,
      existing: { id: existing.id, label: labelOf(existing) },
      alreadyKnown: known.has(parentOrgMatchKey(trimmed)),
    };
  }

  return { kind: 'create', id, name: trimmed, alternativeNames: [trimmed] };
}

/**
 * The `alternativeNames` array an organization should hold once `name` is added
 * to it, or `null` when it already answers to that name.
 *
 * Compared on the match key, not the raw string, so "Rolex SA" is not added
 * beside "Rolex S.A." — but STORED verbatim, because Phase 3's backfill matches
 * legacy `parentOrg` strings against these and needs the spellings brand
 * documents actually carry.
 */
export function withAlias(org: ParentOrganization, name: string): string[] | null {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return null;
  const current = org.alternativeNames ?? [];
  const known = new Set(
    [org.name, org.displayName, ...current].map((v) => parentOrgMatchKey(v ?? '')).filter(Boolean),
  );
  if (known.has(parentOrgMatchKey(trimmed))) return null;
  return [...current, trimmed];
}

/**
 * Anything the CURATOR typed alongside the name, all optional.
 *
 * Only ever curator-entered. Nothing here is inferred or defaulted: a field the
 * curator left blank stays unset, because unset reads as "nobody has said" while
 * a default reads as a fact — the same rule the minimal document was written
 * under.
 */
export interface QuickCreateDetails {
  /** Shown name, when the curator wants it to differ from the document name. */
  displayName?: string;
  country?: string;
  founded?: number;
  /**
   * Further spellings to record beside the planned name — the originally
   * proposed name, when the curator corrected it in the form. Without this, a
   * rename in the mini-form loses the spelling that failed to resolve, and the
   * next enrich run proposes the same unmatched name all over again.
   */
  extraAliases?: string[];
}

/**
 * The document a quick-create writes: a name, the proposed spelling as an
 * alias, and whatever else the curator filled in.
 *
 * Still minimal BY DEFAULT — called with no details it writes exactly what it
 * always did. When this runs off a name the MODEL produced, the curator
 * typically has nothing to add and the organization is fleshed out later from
 * the Parent Organizations tab, where AI Enrich Basics exists for that. When it
 * runs off a name the curator TYPED, they usually do know the country, and
 * making them reopen the record to add it is the friction this avoids.
 */
export function quickCreateDoc(
  plan: Extract<QuickCreatePlan, { kind: 'create' }>,
  details: QuickCreateDetails = {},
): {
  name: string;
  displayName: string;
  alternativeNames: string[];
  country?: string;
  founded?: number;
} {
  const displayName = (details.displayName ?? '').trim() || plan.name;

  // Deduplicated on the match key, stored verbatim: the aliases are what the
  // Phase 3 backfill matches legacy `parentOrg` strings against, so the
  // spellings have to survive as written, but two spellings of one name are
  // noise on the document.
  const alternativeNames: string[] = [];
  const seen = new Set<string>();
  for (const value of [...plan.alternativeNames, ...(details.extraAliases ?? [])]) {
    const trimmed = (value ?? '').trim();
    const key = parentOrgMatchKey(trimmed);
    if (!trimmed || !key || seen.has(key)) continue;
    seen.add(key);
    alternativeNames.push(trimmed);
  }

  const country = (details.country ?? '').trim();
  const founded =
    typeof details.founded === 'number' && Number.isFinite(details.founded)
      ? details.founded
      : undefined;

  return {
    name: plan.name,
    displayName,
    alternativeNames,
    ...(country ? { country } : {}),
    ...(founded !== undefined ? { founded } : {}),
  };
}
